import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Lock, User, AlertCircle, Building2, Shield, ArrowRight } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const res = await login(username, password);
    setLoading(false);

    if (!res.success) {
      setError(res.message || 'Authentication failed. Please check credentials.');
    }
  };

  const selectRoleUsername = (u: string) => {
    setUsername(u);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col justify-between">
      {/* Official Government Top Bar */}
      <div className="bg-[#081B38] text-white px-4 py-2 border-b-2 border-amber-500">
        <div className="max-w-6xl mx-auto flex justify-between items-center text-xs">
          <div className="flex items-center space-x-2">
            <span className="bg-amber-500 text-slate-950 font-bold px-1.5 py-0.5 rounded text-[10px]">GOVT OF HARYANA</span>
            <span className="font-semibold text-slate-200">Department of School Education • Directorate of School Education</span>
          </div>
          <div className="text-slate-300 text-xs hidden sm:block">
            Official District Education Portal • Nuh
          </div>
        </div>
      </div>

      {/* Main Login Card */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md border border-slate-300 overflow-hidden">
          {/* Header Banner - Strict 3 Lines */}
          <div className="bg-[#0F2C59] p-6 text-white text-center relative border-b-4 border-amber-500">
            <div className="w-12 h-12 mx-auto mb-2.5 rounded bg-amber-500/20 border border-amber-400/60 flex items-center justify-center shadow-inner text-amber-400 font-bold text-sm">
              DSE
            </div>
            <h1 className="text-xs md:text-sm font-semibold text-slate-200 uppercase tracking-wide leading-tight">
              Department of School Education, Haryana
            </h1>
            <h2 className="text-sm md:text-base font-bold text-amber-400 mt-0.5 leading-tight">
              District Education Office, Nuh
            </h2>
            <h3 className="text-xs font-medium text-slate-300 mt-0.5 leading-tight">
              Monthly Assessment and SAT Marks Monitoring Portal
            </h3>
          </div>

          {/* Form */}
          <div className="p-6">
            {error && (
              <div className="mb-4 bg-rose-50 border border-rose-300 text-rose-800 text-xs rounded p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                <div>
                  <span className="font-bold">Authentication Error: </span>
                  {error}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Official Username / ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <User className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. deo.nuh or beo.nuh"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Security Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0F2C59] hover:bg-[#1E40AF] text-white font-semibold py-2.5 px-4 rounded text-sm transition duration-150 flex items-center justify-center space-x-2 shadow-sm disabled:opacity-50"
              >
                {loading ? (
                  <span>Authenticating...</span>
                ) : (
                  <>
                    <span>Secure Sign In</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Official Role Presets */}
            <div className="mt-6 pt-5 border-t border-slate-200">
              <div className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2.5 flex items-center justify-between">
                <span>Select Official Role:</span>
                <span className="text-[10px] text-slate-400 font-normal">Select Username</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                {/* DEO */}
                <button
                  type="button"
                  onClick={() => selectRoleUsername('deo.nuh')}
                  className="col-span-2 p-2 rounded bg-amber-50 border border-amber-300 text-amber-950 hover:bg-amber-100 flex items-center justify-between text-left font-medium"
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-amber-700" />
                    <div>
                      <div className="font-bold text-slate-900">DEO Nuh (District Wide)</div>
                      <div className="text-[10px] text-slate-500">deo.nuh</div>
                    </div>
                  </div>
                  <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-bold">DEO</span>
                </button>

                {/* BEOs */}
                <button
                  type="button"
                  onClick={() => selectRoleUsername('beo.nuh')}
                  className="p-1.5 rounded bg-slate-100 border border-slate-300 text-slate-800 hover:bg-slate-200 text-left"
                >
                  <div className="font-bold text-[11px]">BEO Nuh</div>
                  <div className="text-[10px] text-slate-500">beo.nuh</div>
                </button>

                <button
                  type="button"
                  onClick={() => selectRoleUsername('beo.nagina')}
                  className="p-1.5 rounded bg-slate-100 border border-slate-300 text-slate-800 hover:bg-slate-200 text-left"
                >
                  <div className="font-bold text-[11px]">BEO Nagina</div>
                  <div className="text-[10px] text-slate-500">beo.nagina</div>
                </button>

                <button
                  type="button"
                  onClick={() => selectRoleUsername('beo.taoru')}
                  className="p-1.5 rounded bg-slate-100 border border-slate-300 text-slate-800 hover:bg-slate-200 text-left"
                >
                  <div className="font-bold text-[11px]">BEO Taoru</div>
                  <div className="text-[10px] text-slate-500">beo.taoru</div>
                </button>

                <button
                  type="button"
                  onClick={() => selectRoleUsername('beo.fpj')}
                  className="p-1.5 rounded bg-slate-100 border border-slate-300 text-slate-800 hover:bg-slate-200 text-left"
                >
                  <div className="font-bold text-[11px]">BEO FP Jhirka</div>
                  <div className="text-[10px] text-slate-500">beo.fpj</div>
                </button>

                <button
                  type="button"
                  onClick={() => selectRoleUsername('beo.punhana')}
                  className="p-1.5 rounded bg-slate-100 border border-slate-300 text-slate-800 hover:bg-slate-200 text-left"
                >
                  <div className="font-bold text-[11px]">BEO Punhana</div>
                  <div className="text-[10px] text-slate-500">beo.punhana</div>
                </button>

                {/* School */}
                <button
                  type="button"
                  onClick={() => selectRoleUsername('school.nuh001')}
                  className="p-1.5 rounded bg-blue-50 border border-blue-200 text-blue-900 hover:bg-blue-100 text-left"
                >
                  <div className="font-bold text-[11px]">School GSSS Nuh</div>
                  <div className="text-[10px] text-blue-700">school.nuh001</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Official Footer */}
      <footer className="bg-[#081B38] text-slate-400 text-[11px] py-3 text-center border-t border-slate-800">
        <div>District Education Office, Nuh • Department of School Education, Government of Haryana</div>
        <div className="text-slate-500 text-[10px] mt-0.5">National Informatics Centre / Samagra Shiksha Haryana Standards</div>
      </footer>
    </div>
  );
};
